import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut } from "react-icons/fi";
import type { MediaAttachmentLayout } from "../../utils/contentMedia";

const PrevI = FiChevronLeft as React.FC<{ size?: number }>;
const NextI = FiChevronRight as React.FC<{ size?: number }>;
const ZoomInI = FiZoomIn as React.FC<{ size?: number }>;
const ZoomOutI = FiZoomOut as React.FC<{ size?: number }>;

const PDFJS_WORKER = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

type PdfJs = typeof import("pdfjs-dist");
type ViewerMode = "pdfjs" | "iframe";

let pdfjsPromise: Promise<PdfJs> | null = null;

async function loadPdfJs(): Promise<PdfJs> {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist").then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

async function openPdfDocument(pdfjs: PdfJs, url: string) {
  try {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.arrayBuffer();
    if (data.byteLength < 8) throw new Error("Fichier vide");
    return pdfjs.getDocument({ data, isEvalSupported: false }).promise;
  } catch {
    return pdfjs.getDocument({ url, withCredentials: false, isEvalSupported: false }).promise;
  }
}

type Props = {
  url: string;
  layout: MediaAttachmentLayout;
  onLayoutResolved?: (layout: "portrait" | "landscape") => void;
};

export const ContentPdfViewer: React.FC<Props> = ({ url, layout, onLayoutResolved }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [busy, setBusy] = useState(true);
  const [mode, setMode] = useState<ViewerMode>("pdfjs");
  const [resolvedLayout, setResolvedLayout] = useState<"portrait" | "landscape">(
    layout === "landscape" ? "landscape" : "portrait"
  );
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setPdfDoc(null);
    setNumPages(0);
    setMode("pdfjs");

    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const doc = await openPdfDocument(pdfjs, url);
        if (cancelled) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setPage(1);
        setMode("pdfjs");
      } catch (err) {
        console.warn("[ContentPdfViewer] pdf.js failed, using iframe fallback", err);
        if (!cancelled) {
          setMode("iframe");
          setNumPages(0);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  const effectiveLayout = layout === "auto" ? resolvedLayout : layout;

  const renderPage = useCallback(async () => {
    if (mode !== "pdfjs" || !pdfDoc || !canvasRef.current || !wrapRef.current) return;
    const pdfPage = await pdfDoc.getPage(page);
    const base = pdfPage.getViewport({ scale: 1 });
    if (layout === "auto" && page === 1) {
      const next = base.height > base.width ? "portrait" : "landscape";
      setResolvedLayout(next);
      onLayoutResolved?.(next);
    }
    const maxWidth = wrapRef.current.clientWidth || 900;
    const portraitCap = 520;
    const targetWidth =
      effectiveLayout === "portrait" ? Math.min(maxWidth, portraitCap) : maxWidth;
    const scale = (targetWidth / base.width) * zoom;
    const viewport = pdfPage.getViewport({ scale });
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await pdfPage.render({ canvasContext: ctx, viewport, canvas }).promise;
  }, [pdfDoc, page, effectiveLayout, layout, zoom, onLayoutResolved, mode]);

  useEffect(() => {
    if (mode !== "pdfjs") return;
    renderPage().catch((err) => {
      console.warn("[ContentPdfViewer] render failed", err);
      setMode("iframe");
    });
  }, [renderPage, mode]);

  const frameClass =
    effectiveLayout === "portrait"
      ? "content-media__frame content-media__frame--portrait"
      : "content-media__frame content-media__frame--landscape";

  const iframeHeight = effectiveLayout === "portrait" ? "82vh" : "72vh";

  return (
    <div className={`content-media__pdf ${frameClass}`} ref={wrapRef} data-testid="pdf-viewer">
      {mode === "pdfjs" && (
        <div className="content-media__toolbar">
          <button
            type="button"
            className="content-media__toolbtn"
            disabled={page <= 1 || busy}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Page précédente"
          >
            <PrevI size={18} />
          </button>
          <span className="content-media__page-indicator">
            {busy ? "Chargement…" : `Page ${page} / ${numPages || "?"}`}
          </span>
          <button
            type="button"
            className="content-media__toolbtn"
            disabled={!numPages || page >= numPages || busy}
            onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            aria-label="Page suivante"
          >
            <NextI size={18} />
          </button>
          <span className="content-media__toolbar-spacer" />
          <button
            type="button"
            className="content-media__toolbtn"
            onClick={() => setZoom((z) => Math.max(0.6, z - 0.15))}
            aria-label="Zoom arrière"
          >
            <ZoomOutI size={16} />
          </button>
          <button
            type="button"
            className="content-media__toolbtn"
            onClick={() => setZoom((z) => Math.min(2, z + 0.15))}
            aria-label="Zoom avant"
          >
            <ZoomInI size={16} />
          </button>
        </div>
      )}

      <div className={`content-media__pdf-canvas-wrap content-media__pdf-canvas-wrap--${effectiveLayout}`}>
        {mode === "iframe" ? (
          <iframe
            src={`${url}#view=FitH&toolbar=1`}
            title="Document PDF"
            className="content-media__pdf-iframe"
            style={{ height: iframeHeight }}
          />
        ) : busy ? (
          <p className="content-media__loading">Chargement du document…</p>
        ) : (
          <canvas ref={canvasRef} className="content-media__pdf-canvas" />
        )}
      </div>

      <p className="content-media__hint">
        {mode === "pdfjs"
          ? "Parcourez le document page par page — le téléchargement n'est pas requis pour la lecture."
          : "Lecteur intégré du navigateur — utilisez les contrôles pour feuilleter le PDF."}
      </p>
    </div>
  );
};
